import React, { useEffect } from 'react';
import { usePurchaseViewModel } from '@/viewmodels/PurchaseViewModel';
import { PurchaseCard } from '@/components/ui/purchase-card';
import { PaymentMethodModal } from '@/components/ui/payment-method-modal';
import { PaymentProcessingModal } from '@/components/ui/payment-processing-modal';
import { Confetti } from '@/components/ui/confetti';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Coins, Sparkles, Zap } from 'lucide-react';
import { useTranslation } from '@/contexts/I18nContext';

export const PurchasePage: React.FC = () => {
  const viewModel = usePurchaseViewModel();
  const { t } = useTranslation();

  useEffect(() => {
    viewModel.loadData();
  }, []);

  const handleSubscriptionPurchase = (planId: string) => {
    viewModel.initiatePurchase('subscription', planId);
  };

  const handleTokenPackPurchase = (packId: string) => {
    viewModel.initiatePurchase('token', packId);
  };

  const handlePaymentMethodSelect = (provider: 'alipay' | 'polar') => {
    viewModel.selectPaymentMethod(provider);
  };

  const handleCheckPaymentStatus = () => {
    viewModel.pollPaymentStatus();
  };

  const renderLoadingSkeleton = () => (
    <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="p-8 border rounded-2xl bg-white/50 backdrop-blur-sm">
            <Skeleton className="h-8 w-3/4 mb-6" />
            <Skeleton className="h-6 w-full mb-3" />
            <Skeleton className="h-6 w-2/3 mb-6" />
            <Skeleton className="h-16 w-full mb-6" />
            <div className="space-y-3">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
            </div>
            <Skeleton className="h-12 w-full mt-8" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-16">
      <Alert className="max-w-md mx-auto bg-red-50 border-red-200">
        <AlertDescription className="text-red-800">
          {viewModel.error}
        </AlertDescription>
      </Alert>
    </div>
  );

  const renderSubscriptionSection = () => (
    <div className="space-y-12">
      <div className="text-center space-y-6">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
            {t('purchase.subscriptionPlans')}
          </h2>
        </div>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
          {t('purchase.subscriptionDescription')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {viewModel.subscriptionPlans.map((plan, index) => (
          <PurchaseCard
            key={plan.id}
            title={plan.name}
            description={plan.description}
            price={plan.price}
            currency={plan.currency}
            features={plan.features}
            popular={index === 1} // Mark middle plan as popular
            recommended={plan.code === 'premium'}
            buttonText={t('purchase.subscribeNow')}
            onPurchase={() => handleSubscriptionPurchase(plan.id)}
            className="h-full"
          />
        ))}
      </div>
    </div>
  );

  const renderTokenPackSection = () => (
    <div className="space-y-12">
      <div className="text-center space-y-6">
        <div className="flex items-center justify-center gap-3">
          <div className="p-3 rounded-2xl bg-gradient-to-r from-slate-500 to-gray-600 shadow-lg">
            <Coins className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-600 via-gray-600 to-slate-800 bg-clip-text text-transparent">
            {t('purchase.tokenPacks')}
          </h2>
        </div>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
          {t('purchase.tokenPacksDescription')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {viewModel.tokenPacks.map((pack, index) => (
          <PurchaseCard
            key={pack.id}
            title={pack.name}
            description={pack.description}
            price={pack.price}
            currency={pack.currency}
            features={[
              `${pack.token_count.toLocaleString()} ${t('purchase.tokensIncluded')}`,
              t('purchase.noExpirationDate'),
              t('purchase.payOnceUseAnytime'),
              t('purchase.perfectForOccasionalUsers')
            ]}
            popular={index === 1} // Mark middle pack as popular
            buttonText={t('purchase.buyNow')}
            onPurchase={() => handleTokenPackPurchase(pack.id)}
            tokenCount={pack.token_count}
            badge={t('purchase.oneTimePayment')}
            className="h-full"
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-blue-900/20 dark:to-purple-900/20">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-6 py-16">
        {/* Header */}
        {/* <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="p-4 rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 shadow-2xl">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Choose Your Plan
          </h1>
          <p className="text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Unlock the full potential of our translation service with flexible pricing options designed for every need
          </p>
        </div> */}

        {/* Content */}
        {viewModel.loading ? (
          renderLoadingSkeleton()
        ) : viewModel.error ? (
          renderErrorState()
        ) : (
          <div className="space-y-24">
            {renderSubscriptionSection()}
            
            {/* 分隔线 */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
              </div>
              <div className="relative flex justify-center">
                <div className="px-8 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full border border-gray-200 dark:border-gray-700">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
            
            {renderTokenPackSection()}
          </div>
        )}

        {/* Modals */}
        <PaymentMethodModal
          isOpen={viewModel.isPaymentMethodModalVisible}
          onClose={() => viewModel.resetPayment()}
          onSelectMethod={handlePaymentMethodSelect}
          dismissible={viewModel.selectedPaymentProvider !== 'polar' || !viewModel.isPaymentModalVisible}
          loadingMethod={viewModel.loadingPaymentMethod}
        />

        <PaymentProcessingModal
          isOpen={viewModel.isPaymentModalVisible}
          onClose={() => viewModel.resetPayment()}
          paymentProvider={viewModel.selectedPaymentProvider || 'alipay'}
          paymentUrl={viewModel.paymentUrl}
          orderId={viewModel.orderId}
          onCheckStatus={handleCheckPaymentStatus}
          isPolling={viewModel.polling}
          paymentStatus={viewModel.paymentStatus}
        />

        {/* Confetti Animation */}
        <Confetti 
          show={viewModel.showConfetti} 
          onComplete={() => viewModel.setShowConfetti(false)}
        />
      </div>
    </div>
  );
}; 